import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Plus,
  Edit3,
  Trash2,
  GripVertical,
  ChevronDown,
  Search,
  X,
  Briefcase,
  Layers,
  Tag,
  MessageSquareText,
  AlertCircle,
  FileQuestion,
  Loader2,
  Filter,
  Check,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Tooltip,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import ConfirmationDialog from '../ConfirmationDialog';
import { useNotification } from '../../../interviewAI/NotificationContext';
import * as api from '../../services/predefinedQuestionsApi';

const DIFFICULTY_OPTIONS = ['Basic', 'Intermediate', 'Advanced'];

const emptyForm = () => ({
  question: '',
  skill: '',
  difficulty: 'Basic',
  category: '',
  follow_up_questions: [],
});

/* ------------------------------------------------------------------ */
/*  Small presentational pieces                                        */
/* ------------------------------------------------------------------ */

const DifficultyBadge = ({ difficulty }) => {
  const style = {
    Basic: 'settings-predefined-difficulty-badge--basic',
    Intermediate: 'settings-predefined-difficulty-badge--intermediate',
    Advanced: 'settings-predefined-difficulty-badge--advanced',
  }[difficulty] || '';

  return (
    <span className={`settings-predefined-difficulty-badge ${style}`}>
      {difficulty || 'Unset'}
    </span>
  );
};

const MetaChip = ({ icon: Icon, label }) => (
  <span className="settings-predefined-meta-chip inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium max-w-full">
    <Icon className="w-3.5 h-3.5 shrink-0 text-[var(--text-muted)]" />
    <span className="truncate">{label}</span>
  </span>
);

/* ------------------------------------------------------------------ */
/*  Job Opening picker                                                 */
/* ------------------------------------------------------------------ */

const JobOpeningPicker = ({
  templates,
  selectedTemplate,
  selectedQuestionCount,
  loadingTemplates,
  onSelect,
}) => {
  const [inputValue, setInputValue] = useState('');

  return (
    <section className="settings-predefined-job-card rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)]">
      <div className="settings-predefined-job-heading flex items-center gap-2">
        <span className="settings-predefined-job-icon" aria-hidden="true">
          <Briefcase />
        </span>
        <span className="text-sm font-semibold text-[var(--text-primary)]">Job Opening</span>
      </div>
      <div className="settings-predefined-job-body">
        <Autocomplete
          className="settings-predefined-job-picker"
          disablePortal
          openOnFocus={false}
          options={templates}
          loading={loadingTemplates}
          value={selectedTemplate}
          inputValue={inputValue}
          isOptionEqualToValue={(opt, val) => opt.templateId === val?.templateId}
          getOptionLabel={(opt) => opt?.templateName || ''}
          filterOptions={(options, { inputValue: searchValue }) => {
            const query = searchValue.trim().toLowerCase();
            if (!query) return options;
            return options.filter((option) =>
              `${option.templateName || ''} ${option.reqId || ''}`.toLowerCase().includes(query)
            );
          }}
          onInputChange={(_, value, reason) => {
            setInputValue(reason === 'input' || reason === 'clear' ? value : '');
          }}
          onChange={(_, value) => {
            setInputValue('');
            if (value) onSelect(value);
          }}
          noOptionsText="No job openings found"
          renderOption={(props, option) => {
            const isSelected = selectedTemplate?.templateId === option.templateId;
            return (
              <li {...props}>
                <div className="settings-predefined-job-option-content flex items-center justify-between w-full gap-3 py-0.5">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{option.templateName}</div>
                    <div className="text-xs text-gray-500">REQ {option.reqId}</div>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                    <span>{option.questionCount ?? 0} Qs</span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-[var(--primary)]" />}
                  </span>
                </div>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search job openings by name or REQ ID..."
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="w-4 h-4 text-gray-400" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loadingTemplates ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {selectedTemplate && (
          <div className="settings-predefined-selected-job mt-4 flex items-center justify-between flex-wrap gap-3 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] px-4 py-3">
            <div className="min-w-0">
              <div className="font-semibold text-[var(--text-primary)] truncate">
                {selectedTemplate.templateName}
              </div>
              <div className="text-xs text-[var(--text-muted)] mt-0.5">REQ {selectedTemplate.reqId}</div>
            </div>
            <span className="shrink-0 px-3 py-1 rounded-full bg-[var(--primary)]/10 text-[var(--primary)] text-sm font-semibold whitespace-nowrap">
              {selectedQuestionCount} question{selectedQuestionCount === 1 ? '' : 's'}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Question accordion item                                            */
/* ------------------------------------------------------------------ */

const QuestionItem = ({
  q,
  questionNumber,
  originalIndex,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  reorderEnabled,
  isDragging,
  isDragOver,
  dragHandlers,
}) => {
  const followUps = q.follow_up_questions || [];

  return (
    <div
      {...(reorderEnabled ? dragHandlers : {})}
      className={`settings-predefined-question-row rounded-xl border bg-[var(--bg-card)] transition-all ${
        isDragging ? 'opacity-50 border-[var(--primary)]' : 'border-[var(--border-color)]'
      } ${isDragOver ? 'ring-2 ring-[var(--primary)] ring-offset-2 ring-offset-[var(--bg-page)]' : ''}`}
    >
      <div className="flex items-start gap-2 p-4">
        {reorderEnabled ? (
          <Tooltip title="Drag to reorder">
            <div
              className="cursor-grab active:cursor-grabbing p-1.5 -ml-1.5 mt-0.5 text-gray-400 hover:text-gray-600 shrink-0"
              aria-label={`Reorder question ${questionNumber}`}
            >
              <GripVertical className="w-4 h-4" />
            </div>
          </Tooltip>
        ) : (
          <div className="w-4 shrink-0" />
        )}

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          className="settings-predefined-question-toggle flex-1 min-w-0 text-left flex items-start gap-3 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2"
        >
          <span className="mt-0.5 text-xs font-bold text-[var(--text-muted)] shrink-0 w-6">
            Q{questionNumber}
          </span>
          <span
            className={`text-sm font-medium text-[var(--text-primary)] break-words ${
              isExpanded ? '' : 'line-clamp-2'
            }`}
          >
            {q.question}
          </span>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          <Tooltip title="Edit question">
            <button
              onClick={() => onEdit(originalIndex)}
              aria-label={`Edit question ${questionNumber}`}
              className="settings-predefined-action-btn"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip title="Delete question">
            <button
              onClick={() => onDelete(originalIndex)}
              aria-label={`Delete question ${questionNumber}`}
              className="settings-predefined-action-btn settings-predefined-action-btn--danger"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </Tooltip>
          <button
            onClick={onToggle}
            aria-label={isExpanded ? 'Collapse question' : 'Expand question'}
            aria-expanded={isExpanded}
            className="settings-predefined-action-btn settings-predefined-action-btn--ghost"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>

      <div className="pl-[3.25rem] pr-4 pb-3 -mt-1 flex flex-wrap items-center gap-1.5">
        <DifficultyBadge difficulty={q.difficulty} />
        {q.skill && <MetaChip icon={Layers} label={q.skill} />}
        {q.category && <MetaChip icon={Tag} label={q.category} />}
        {followUps.length > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-[var(--text-muted)]">
            <MessageSquareText className="w-3.5 h-3.5" />
            {followUps.length} follow-up{followUps.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {isExpanded && (
        <div className="pl-[3.25rem] pr-4 pb-4">
          {followUps.length > 0 ? (
            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-input)]/60 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
                <MessageSquareText className="w-3.5 h-3.5" />
                Follow-up Questions
              </div>
              <ol className="space-y-2">
                {followUps.map((f, idx) => (
                  <li key={idx} className="flex gap-2 text-sm text-[var(--text-primary)]">
                    <span className="text-[var(--text-muted)] font-medium shrink-0">{idx + 1}.</span>
                    <span className="break-words">{f}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <div className="text-sm text-[var(--text-muted)] italic">No follow-up questions added.</div>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Add / Edit form dialog                                             */
/* ------------------------------------------------------------------ */

const QuestionFormDialog = ({ open, editingIndex, formData, setFormData, errors, saving, onClose, onSave, skillOptions, categoryOptions }) => (
  <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="settings-predefined-form-title" aria-describedby="settings-predefined-form-description" className="settings-predefined-dialog-root" PaperProps={{ className: "settings-predefined-dialog-paper" }}>
    <header className="settings-predefined-form-header">
      <span className="settings-predefined-form-icon" aria-hidden="true"><MessageSquareText size={23} /></span>
      <div><h2 id="settings-predefined-form-title">{editingIndex === null ? 'Add Predefined Question' : 'Edit Predefined Question'}</h2><p id="settings-predefined-form-description">Configure an interview question and optional follow-up questions.</p></div>
      <button type="button" onClick={onClose} disabled={saving} aria-label="Close predefined question form"><X size={20} /></button>
    </header>
    <DialogContent className="settings-predefined-form-content">
      <div className="settings-predefined-question-field"><label htmlFor="settings-predefined-question-input">Question <span aria-hidden="true">*</span></label>
        <TextField id="settings-predefined-question-input" placeholder="Enter your interview question..." multiline fullWidth minRows={4} value={formData.question} onChange={e => setFormData({ ...formData, question: e.target.value })} error={!!errors.question} helperText={errors.question || undefined} inputProps={{ 'aria-required': true }} />
      </div>
      <div className="settings-predefined-form-pair">
        {[{key:'skill',label:'Skill',Icon:Layers,options:skillOptions},{key:'category',label:'Category',Icon:Tag,options:categoryOptions}].map(({key,label,Icon,options}) => <div key={key}>
          <label htmlFor={`settings-predefined-${key}-input`}>{label} <span aria-hidden="true">*</span></label>
          <Autocomplete id={`settings-predefined-${key}-input`} freeSolo forcePopupIcon options={options || []} value={formData[key]} inputValue={formData[key]} onChange={(_, value) => setFormData({ ...formData, [key]: value || '' })} onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') setFormData({ ...formData, [key]: value }); }} componentsProps={{ paper:{className:'settings-predefined-form-options'} }} renderInput={params => <TextField {...params} placeholder={`Enter or choose a ${key}`} error={!!errors[key]} helperText={errors[key] || undefined} inputProps={{...params.inputProps,'aria-required':true}} InputProps={{...params.InputProps,startAdornment:<InputAdornment position="start"><Icon size={16}/></InputAdornment>}} />} />
        </div>)}
      </div>
      <fieldset className="settings-predefined-form-difficulty"><legend>Difficulty</legend><div>
        {DIFFICULTY_OPTIONS.map(option => <label key={option} className={formData.difficulty === option ? 'is-selected' : ''}><input type="radio" name="settings-predefined-difficulty" value={option} checked={formData.difficulty === option} onChange={() => setFormData({ ...formData, difficulty: option })}/>{option}</label>)}
      </div></fieldset>
      <section className="settings-predefined-form-followups">
        <header><h3>Follow-up Questions</h3><button type="button" onClick={() => setFormData({ ...formData, follow_up_questions:[...(formData.follow_up_questions || []), ''] })}><Plus size={15} />Add Follow-up</button></header>
        {(!formData.follow_up_questions || formData.follow_up_questions.length === 0) ? <div className="settings-predefined-followups-empty"><MessageSquareText size={24}/><span>No follow-up questions yet</span><p>Add optional questions to explore the candidate's response.</p></div> : <div className="settings-predefined-followups-panel">
          <div className="settings-predefined-followups-panel-heading"><MessageSquareText size={16}/><span>Follow-up Questions</span><b>{formData.follow_up_questions.length}</b></div>
          {formData.follow_up_questions.map((followup,index) => <div className="settings-predefined-followup-edit-row" key={index}><span>{index + 1}</span><TextField fullWidth multiline minRows={1} size="small" placeholder={`Follow-up question ${index + 1}`} value={followup} inputProps={{'aria-label':`Follow-up question ${index + 1}`}} onChange={e => { const arr=[...formData.follow_up_questions];arr[index]=e.target.value;setFormData({...formData,follow_up_questions:arr}); }} /><button type="button" aria-label={`Remove follow-up question ${index + 1}`} onClick={() => { const arr=[...(formData.follow_up_questions || [])];arr.splice(index,1);setFormData({...formData,follow_up_questions:arr}); }}><Trash2 size={16}/></button></div>)}
        </div>}
      </section>
    </DialogContent>
    <DialogActions className="settings-predefined-form-actions"><Button onClick={onClose} disabled={saving} startIcon={<X size={15}/>}>Cancel</Button><Button disabled={saving} onClick={onSave} variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit"/> : <Check size={16}/>}>{saving ? 'Saving...' : 'Save Changes'}</Button></DialogActions>
  </Dialog>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const PredefinedQuestions = ({ canEditSettings, canViewSettings }) => {
  const { notify } = useNotification();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionCount, setQuestionCount] = useState(0);

  // Search / filter
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('All');
  const [expandedIndices, setExpandedIndices] = useState(() => new Set());

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({ open: false });

  // Drag state
  const dragIndexRef = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    if (!canViewSettings) return;
    loadPlannedTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewSettings]);

  const loadPlannedTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const res = await api.getPlannedTemplates();
      const data = res?.success ? res.data || [] : [];
      setTemplates(data);
      if (data.length > 0) {
        loadQuestionBank(data[0]);
      }
    } catch (err) {
      console.error(err);
      notify('error', 'Failed to load job openings');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const resetQuestionUIState = () => {
    setSearchQuery('');
    setDifficultyFilter('All');
    setExpandedIndices(new Set());
    setIsReordering(false);
  };

  const loadQuestionBank = async (template) => {
    if (!template) return;
    try {
      setSelectedTemplate(template);
      setQuestionCount(template.questionCount ?? 0);
      resetQuestionUIState();
      setLoadingQuestions(true);
      const res = await api.getQuestionBank(template.templateId);
      if (res?.success && res.data) {
        setQuestions(res.data.questions || []);
        setQuestionCount(res.data.questionCount || (res.data.questions || []).length);
      } else {
        setQuestions([]);
        setQuestionCount(0);
      }
    } catch (err) {
      console.error(err);
      notify('error', 'Failed to load question bank');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const openAdd = () => {
    setEditingIndex(null);
    setFormData(emptyForm());
    setFormErrors({});
    setFormOpen(true);
  };

  const openEdit = (index) => {
    const q = questions[index];
    setEditingIndex(index);
    setFormData({
      question: q.question || '',
      skill: q.skill || '',
      difficulty: DIFFICULTY_OPTIONS.includes(q.difficulty) ? q.difficulty : 'Basic',
      category: q.category || '',
      follow_up_questions: q.follow_up_questions ? [...q.follow_up_questions] : [],
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.question?.trim()) errors.question = 'Question is required';
    if (!formData.skill?.trim()) errors.skill = 'Skill is required';
    if (!formData.category?.trim()) errors.category = 'Category is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveQuestion = async () => {
    if (!selectedTemplate) return notify('error', 'Select a Job Opening first');
    if (!validateForm()) {
      notify('error', 'Please fill in all required fields');
      return;
    }
    const cleanedFollowUps = (formData.follow_up_questions || []).map((f) => f.trim()).filter(Boolean);
    const payload = { ...formData, follow_up_questions: cleanedFollowUps };

    try {
      setSaving(true);
      if (editingIndex === null) {
        const res = await api.addQuestion(selectedTemplate.templateId, payload);
        if (res?.success && res.data) {
          setQuestions(res.data.questions || []);
          setQuestionCount(res.data.questionCount || (res.data.questions || []).length);
          notify('success', 'Question added');
        }
      } else {
        const res = await api.updateQuestion(selectedTemplate.templateId, editingIndex, payload);
        if (res?.success && res.data) {
          setQuestions(res.data.questions || []);
          setQuestionCount(res.data.questionCount || (res.data.questions || []).length);
          notify('success', 'Question updated');
        }
      }
      setFormOpen(false);
    } catch (err) {
      console.error(err);
      notify('error', err?.message || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (index) => {
    setConfirmDialog({ open: true, index });
  };

  const doDelete = async (index) => {
    if (!selectedTemplate) return;
    try {
      setLoadingQuestions(true);
      const res = await api.deleteQuestion(selectedTemplate.templateId, index);
      if (res?.success && res.data) {
        setQuestions(res.data.questions || []);
        setQuestionCount(res.data.questionCount || (res.data.questions || []).length);
        notify('success', 'Question deleted');
      }
    } catch (err) {
      console.error(err);
      notify('error', 'Failed to delete question');
    } finally {
      setLoadingQuestions(false);
      setConfirmDialog({ open: false });
    }
  };

  // Drag handlers (HTML5) — only active when list is unfiltered
  const onDragStart = (e, idx) => {
    dragIndexRef.current = idx;
    setDraggingIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== idx) setDragOverIndex(idx);
  };

  const onDragEnd = () => {
    dragIndexRef.current = null;
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const onDrop = (e, toIndex) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    const to = toIndex;
    onDragEnd();
    if (from === null || from === to) return;
    const copy = [...questions];
    const item = copy.splice(from, 1)[0];
    copy.splice(to, 0, item);
    setQuestions(copy);
    setIsReordering(true);
  };

  const saveOrder = async () => {
    if (!selectedTemplate) return;
    try {
      setSavingOrder(true);
      const payload = questions.map((q) => ({
        question: q.question,
        skill: q.skill,
        difficulty: q.difficulty,
        category: q.category,
        follow_up_questions: q.follow_up_questions || [],
      }));
      const res = await api.reorderQuestions(selectedTemplate.templateId, payload);
      if (res?.success && res.data) {
        setQuestions(res.data.questions || []);
        setQuestionCount(res.data.questionCount || (res.data.questions || []).length);
        notify('success', 'Order saved');
        setIsReordering(false);
      }
    } catch (err) {
      console.error(err);
      notify('error', 'Failed to save order');
      if (selectedTemplate) loadQuestionBank(selectedTemplate);
    } finally {
      setSavingOrder(false);
    }
  };

  const discardOrder = () => {
    if (selectedTemplate) loadQuestionBank(selectedTemplate);
  };

  const toggleExpanded = (index) => {
    setExpandedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const hasActiveFilters = searchQuery.trim().length > 0 || difficultyFilter !== 'All';
  const reorderEnabled = !hasActiveFilters;

  const filteredQuestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return questions
      .map((question, originalIndex) => ({ question, originalIndex }))
      .filter(({ question }) => {
        if (difficultyFilter !== 'All' && question.difficulty !== difficultyFilter) return false;
        if (!q) return true;
        return (
          question.question?.toLowerCase().includes(q) ||
          question.skill?.toLowerCase().includes(q) ||
          question.category?.toLowerCase().includes(q)
        );
      });
  }, [questions, searchQuery, difficultyFilter]);

  if (!canViewSettings) {
    return null;
  }

  return (
    <div className="settings-content-predefined text-left">
      <div className="settings-predefined-page-header rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)] shadow-sm px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0 text-left">
          <h2 className="text-2xl font-bold text-[var(--text-primary)] leading-tight text-left m-0">Predefined Questions</h2>
          <p className="text-[var(--text-secondary)] mt-1 text-sm text-left m-0">
            Build and manage the question bank interviewers draw from for each Job Opening.
          </p>
        </div>
        {canEditSettings && (
          <Tooltip title={selectedTemplate ? '' : 'Select a Job Opening first'}>
            <span>
              <button
                disabled={!selectedTemplate}
                onClick={openAdd}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-colors shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 ${
                  selectedTemplate
                    ? 'bg-[var(--primary)] text-white hover:bg-[var(--primary-dark)] shadow-sm'
                    : 'bg-[var(--bg-input)] text-[var(--text-muted)] cursor-not-allowed'
                }`}
              >
                <Plus className="w-4 h-4" /> Add Question
              </button>
            </span>
          </Tooltip>
        )}
      </div>

      <JobOpeningPicker
        templates={templates}
        selectedTemplate={selectedTemplate}
        selectedQuestionCount={questionCount}
        loadingTemplates={loadingTemplates}
        onSelect={loadQuestionBank}
      />

      {!selectedTemplate ? (
        <div className="rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--bg-card)] py-16 px-6 flex flex-col items-center text-center gap-3">
          <div className="p-3 rounded-full bg-[var(--bg-input)]">
            <FileQuestion className="w-6 h-6 text-[var(--text-muted)]" />
          </div>
          <div className="font-semibold text-[var(--text-primary)]">Select a Job Opening to get started</div>
          <p className="text-sm text-[var(--text-secondary)] max-w-sm">
            Choose a Job Opening above to view, search, and manage its predefined interview questions.
          </p>
        </div>
      ) : (
        <section className="settings-predefined-bank rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)] overflow-hidden">
          {/* Toolbar */}
          <div className="settings-predefined-toolbar p-4 border-b border-[var(--border-color)] flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions by text, skill, or category..."
                className="w-full pl-9 pr-8 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-input)] text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              <Filter className="w-4 h-4 text-[var(--text-muted)] mr-0.5" />
              {['All', ...DIFFICULTY_OPTIONS].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setDifficultyFilter(opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] ${
                    difficultyFilter === opt
                      ? 'settings-predefined-difficulty-selected'
                      : 'bg-[var(--bg-input)] text-[var(--text-secondary)] border-[var(--border-color)] hover:border-[var(--primary)]'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {isReordering && (
            <div className="px-4 py-3 bg-[var(--primary)]/5 border-b border-[var(--border-color)] flex items-center justify-between flex-wrap gap-3">
              <span className="text-sm font-medium text-[var(--text-primary)]">
                Question order has unsaved changes
              </span>
              <div className="flex items-center gap-2">
                <Button size="small" onClick={discardOrder} disabled={savingOrder}>
                  Discard
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={saveOrder}
                  disabled={savingOrder}
                  startIcon={savingOrder ? <CircularProgress size={14} color="inherit" /> : null}
                >
                  {savingOrder ? 'Saving...' : 'Save Order'}
                </Button>
              </div>
            </div>
          )}

          {hasActiveFilters && (
            <div className="px-4 py-2 text-xs text-[var(--text-muted)] bg-[var(--bg-input)]/50 border-b border-[var(--border-color)]">
              Clear filters to reorder questions.
            </div>
          )}

          {/* Question list */}
          <div className="settings-predefined-question-list p-4">
            {loadingQuestions ? (
              <div className="flex items-center justify-center gap-2 py-16 text-[var(--text-secondary)]">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading questions...
              </div>
            ) : questions.length === 0 ? (
              <div className="flex flex-col items-center text-center gap-3 py-16">
                <div className="p-3 rounded-full bg-[var(--bg-input)]">
                  <FileQuestion className="w-6 h-6 text-[var(--text-muted)]" />
                </div>
                <div className="font-semibold text-[var(--text-primary)]">No questions yet</div>
                <p className="text-sm text-[var(--text-secondary)] max-w-sm">
                  Add your first predefined question to build this Job Opening's question bank.
                </p>
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="flex flex-col items-center text-center gap-3 py-16">
                <div className="p-3 rounded-full bg-[var(--bg-input)]">
                  <AlertCircle className="w-6 h-6 text-[var(--text-muted)]" />
                </div>
                <div className="font-semibold text-[var(--text-primary)]">No matching questions</div>
                <p className="text-sm text-[var(--text-secondary)]">
                  Try a different search term or clear the difficulty filter.
                </p>
              </div>
            ) : (
              <div className="settings-predefined-rows">
                {filteredQuestions.map(({ question: q, originalIndex }, displayIndex) => (
                  <div
                    key={originalIndex}
                    onDragOver={reorderEnabled ? (e) => onDragOver(e, originalIndex) : undefined}
                    onDrop={reorderEnabled ? (e) => onDrop(e, originalIndex) : undefined}
                  >
                    <QuestionItem
                      q={q}
                      questionNumber={displayIndex + 1}
                      originalIndex={originalIndex}
                      isExpanded={expandedIndices.has(originalIndex)}
                      onToggle={() => toggleExpanded(originalIndex)}
                      onEdit={openEdit}
                      onDelete={confirmDelete}
                      reorderEnabled={reorderEnabled}
                      isDragging={draggingIndex === originalIndex}
                      isDragOver={reorderEnabled && dragOverIndex === originalIndex && draggingIndex !== originalIndex}
                      dragHandlers={{
                        draggable: true,
                        onDragStart: (e) => onDragStart(e, originalIndex),
                        onDragEnd,
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <QuestionFormDialog
        open={formOpen}
        editingIndex={editingIndex}
        formData={formData}
        setFormData={setFormData}
        errors={formErrors}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onSave={saveQuestion}
        skillOptions={[...new Set(questions.map(question => question.skill).filter(Boolean))]}
        categoryOptions={[...new Set(questions.map(question => question.category).filter(Boolean))]}
      />

      <ConfirmationDialog
        open={confirmDialog.open}
        title="Delete question?"
        message="This question will be permanently removed from this question bank."
        onClose={() => setConfirmDialog({ open: false })}
        onConfirm={() => doDelete(confirmDialog.index)}
        confirmText="Delete Question"
      />
    </div>
  );
};

export default PredefinedQuestions;